import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import {
  AgencySettings_VehicleTypesEnum,
  AgencySettings_VehiclePropulsionsEnum,
  Provider,
  VehicleAggregation,
} from 'src/api/gen/api'
import {
  getProviderSection,
  getVehicleTypeSection,
  getPropulsionTypeSection,
  getStatusSection,
} from 'src/commons/filters/sections'
import { ISection } from 'src/commons/filters/types'
import { getProviderEntries, getEnumEntries } from 'src/commons/filters/utils'
import { enumsTranslationKeys, withFormattedCount } from 'src/commons/i18nEnums'
import { FilterPanelValue } from 'src/commons/map/types'
import { Can } from 'src/commons/permissions'
import { SidePanelButton } from 'src/components/SidePanel'
import CollapsibleFilter from 'src/muiv5/components/commons/SidePanel/Filters/BaseFilter/CollapsibleFilter'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'

import { ISupervisionMapFilters } from './types'

export interface IProps {
  addPolygon?: () => void
  areas?: string[]
  defaultFilters: ISupervisionMapFilters
  deviceCategories: AgencySettings_VehicleTypesEnum[]
  devicePropulsions: AgencySettings_VehiclePropulsionsEnum[]
  displayProviderColor?: boolean
  initialFilters: ISupervisionMapFilters
  onExport?: () => void
  providers: Provider[]
  removePolygon?: () => void
  vehiclesCount: number
  vehiclesFacets: VehicleAggregation
}

const SupervisionFilterPanel = forwardRef(function (props: IProps, ref) {
  const {
    areas,
    providers,
    deviceCategories,
    devicePropulsions,
    vehiclesCount,
    vehiclesFacets,
    displayProviderColor,
    onExport,
  } = props
  const { t } = useTranslation()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  function onFilterChange(key: string, newValue: FilterPanelValue) {
    if (newValue === undefined) {
      console.error(`Unable to apply undefined filter on supervision map, filter key=${key}`)
      return
    }

    setFilters({ ...filters, [key]: newValue })
  }

  function onFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('telemetryAfter', value)
  }

  function onToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('telemetryBefore', value)
  }

  function onBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    setFilters((pendingFilters) => ({
      ...pendingFilters,
      telemetryAfter: valueFrom,
      telemetryBefore: valueTo,
    }))
  }

  const onReset = function () {
    setFilters(props.defaultFilters)
  }

  const vehicleTypeEntries = getEnumEntries(
    deviceCategories,
    enumsTranslationKeys.DeviceType,
    vehiclesFacets.categories
  )
  const vehicleTypeSection = getVehicleTypeSection({ entries: vehicleTypeEntries })

  const propulsionTypeEntries = getEnumEntries(
    devicePropulsions,
    enumsTranslationKeys.DevicePropulsion,
    vehiclesFacets.propulsions
  )
  const propulsionTypeSection = getPropulsionTypeSection({ entries: propulsionTypeEntries })

  const providerEntries = getProviderEntries(providers, vehiclesFacets.providers, displayProviderColor)
  const providerSection = getProviderSection({ entries: providerEntries })

  const statusEntries = Object.entries(enumsTranslationKeys.VehicleStates).map(([id, name]) => ({
    id,
    label: upperFirst(t(name)),
    value: vehiclesFacets.statuses[id],
  }))
  const statusSection = getStatusSection({ entries: statusEntries })

  // we want to mask the provider section in the case of a user with only one provider access
  // logic should be moved to utils.commons if it is reused somewhere else (which should be the case for provider users)
  const checkboxesSections: ISection[] = [vehicleTypeSection, propulsionTypeSection, statusSection]
  if (providerSection.entries.length > 1) {
    checkboxesSections.splice(2, 0, providerSection) // insert provider section right after propulsion section
  }

  return (
    <SidePanel
      headerProps={{
        information: t('vehiclesWithCount', withFormattedCount(vehiclesCount)),
        onExport,
        title: t('filtersPanel.title.default'),
        onReset,
      }}
    >
      <CheckboxListFilter
        filterId={'category'}
        items={vehicleTypeEntries}
        values={filters['category']}
        title={upperFirst(t('filtersPanel.vehicleType'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'propulsion'}
        items={propulsionTypeEntries}
        values={filters['propulsion']}
        title={upperFirst(t('filtersPanel.propulsionType'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'provider'}
        items={providerEntries}
        values={filters['provider']}
        title={upperFirst(t('filtersPanel.provider'))}
        onFilterChange={onFilterChange}
        displayCheckbox
        defaultExpanded
      />
      <CheckboxListFilter
        filterId={'status'}
        items={statusEntries}
        values={filters['status']}
        title={upperFirst(t('filtersPanel.status'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <TimeRangeFilterPro
        filterId={'telemetry'}
        format="L"
        values={{
          from: filters.telemetryAfter,
          to: filters.telemetryBefore,
        }}
        title={upperFirst(t('vehicles.lastTelemetryDate'))}
        onFromChanged={onFromChanged}
        onToChanged={onToChanged}
        onBothChanged={onBothChanged}
        disableFuture
      />
      <Can I="read" an="area">
        {areas && (
          <CheckboxListFilter
            filterId={'areas'}
            title={upperFirst(t('vehicles.zones'))}
            items={areas.map((area) => ({ id: area, label: area }))}
            values={filters.areas}
            onFilterChange={onFilterChange}
            unique
            autocomplete
            nullable
          />
        )}
      </Can>
      {(props.addPolygon || props.removePolygon) && (
        <CollapsibleFilter filterId={'drawPolygon'} title={t('Draw polygon')} defaultExpanded>
          {filters.polygon ? (
            <SidePanelButton label={t('Remove polygon')} onClick={props.removePolygon} />
          ) : (
            <SidePanelButton label={t('Add polygon')} onClick={props.addPolygon} />
          )}
        </CollapsibleFilter>
      )}
    </SidePanel>
  )
})

export default SupervisionFilterPanel
